import { Navigate, Outlet, useLocation } from "react-router-dom";

import PageLoader from "@/common/components/PageLoader";
import { useAuth } from "@/common/hooks/useAuth";
import AuthLayout from "@/common/layouts/AuthLayout";
import PageWrapper from "@/common/layouts/PageWrapper";

/** Validates that a returnUrl is a safe relative path (guards against open redirects). */
function getSafeReturnUrl(raw: string | null): string | null {
  if (!raw) return null;
  // Must be a relative path starting with "/" but not "//" or "/\"
  if (!(/^\/[^/\\]/.test(raw) || raw === "/")) return null;

  // Onboarding/signup-only destinations (wizard, plan picker, org selector) are
  // tied to the specific brand-new-signup flow that produced them, not to any
  // authenticated user in general. A stale/bookmarked/typo'd URL still carrying
  // `?returnUrl=/onboarding/wizard` (e.g. left over from `OnboardingStatus`'s
  // "already verified? sign in" link) must NOT hijack a later, unrelated
  // existing user's login and drop them on the "invite your team" step instead
  // of the dashboard (issue #1826). Only allow genuine protected-app routes.
  if (/^\/onboarding(\/|$)/.test(raw) || raw === "/select-org") return null;

  return raw;
}

export const PublicRoute = () => {
  const { isAuthenticated, isSuperAdmin, isAppReady } = useAuth();
  const location = useLocation();

  // Wait for auth state to resolve before making redirect decisions (prevents flash)
  if (!isAppReady) return <PageLoader />;

  if (isAuthenticated) {
    const params = new URLSearchParams(location.search);
    const safeReturn = getSafeReturnUrl(params.get("returnUrl"));

    // SuperAdmin always lands on the admin panel
    if (isSuperAdmin) {
      return (
        <Navigate
          to={safeReturn?.startsWith("/admin") ? safeReturn : "/admin"}
          replace
        />
      );
    }

    return <Navigate to={safeReturn || "/dashboard"} replace />;
  }
  return (
    <AuthLayout>
      <PageWrapper>
        <Outlet />
      </PageWrapper>
    </AuthLayout>
  );
};
